import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { StepProduct, StepStyle, StepFabric, StepWindows, StepAddons } from "./EstimatorSteps";
import { EstimatorResult } from "./EstimatorResult";

const STEPS = ["Product", "Style & Hardware", "Fabric & Function", "Windows & Size", "Add-ons", "Your Estimate"];
const INITIAL = { product: "", style: "", hardware: "", tier: "", fn: "", windows: 3, size: "", addons: ["Professional Installation"] };

const canNext = (i, s) => [!!s.product, !!s.style && !!s.hardware, !!s.tier && !!s.fn, !!s.size, true, false][i];

export const Estimator = () => {
  const [step, setStep] = useState(0);
  const [s, setS] = useState(INITIAL);
  const [dir, setDir] = useState(1);
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const go = (n) => { setDir(n > step ? 1 : -1); setStep(n); };

  const body = [
    <StepProduct s={s} set={set} />, <StepStyle s={s} set={set} />, <StepFabric s={s} set={set} />,
    <StepWindows s={s} set={set} />, <StepAddons s={s} set={set} />,
    <EstimatorResult s={s} onReset={() => { setS(INITIAL); go(0); }} />,
  ][step];

  return (
    <section id="estimator" className="py-24 lg:py-36 bg-ink text-linen grain" data-testid="estimator-section">
      <div className="container-x">
        <SectionHead dark eyebrow="Services & Pricing · Instant Estimator" title="Know your number before anyone knocks on your door." desc="Six quick choices. A transparent price range, in Australian dollars, based on real Melbourne supply and install rates." />

        <Reveal delay={0.2} className="mt-14">
          <ol className="flex flex-wrap gap-y-3 border-b border-line-dark" data-testid="estimator-progress">
            {STEPS.map((label, i) => (
              <li key={label} className="flex-1 min-w-[120px]">
                <button type="button" disabled={i > step} onClick={() => go(i)} data-testid={`estimator-step-tab-${i + 1}`}
                  className={`w-full text-left pb-4 border-b-2 -mb-px transition-colors duration-300 ${i === step ? "border-brass" : i < step ? "border-linen/30" : "border-transparent"} ${i > step ? "cursor-default" : "hover:text-brass"}`}>
                  <span className={`block font-mono text-[10px] tracking-[0.2em] ${i <= step ? "text-brass" : "text-linen/30"}`}>0{i + 1}</span>
                  <span className={`block text-xs sm:text-[13px] mt-1 ${i === step ? "text-linen" : "text-linen/50"}`}>{label}</span>
                </button>
              </li>
            ))}
          </ol>
        </Reveal>

        <div className="mt-12 min-h-[380px] relative">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div key={step} custom={dir} initial={{ opacity: 0, x: dir * 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -40 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} data-testid={`estimator-step-${step + 1}`}>
              {step < 5 && <h3 className="font-serif text-2xl sm:text-3xl mb-8">{["Choose your product type", `Choose a ${s.product?.toLowerCase().replace(/s$/, "")} style and hardware`, "Choose fabric tier and function", "How many windows, and how big?", "Any add-on services?"][step]}</h3>}
              {body}
            </motion.div>
          </AnimatePresence>
        </div>

        {step < 5 && (
          <div className="mt-12 flex items-center justify-between gap-4 border-t border-line-dark pt-8">
            <button type="button" onClick={() => go(step - 1)} disabled={step === 0} className="btn-ghost disabled:opacity-30 disabled:hover:translate-y-0" data-testid="estimator-back-btn"><ArrowLeft size={16} /> Back</button>
            <button type="button" onClick={() => go(step + 1)} disabled={!canNext(step, s)} className="btn-brass disabled:opacity-40 disabled:hover:translate-y-0" data-testid="estimator-next-btn">
              {step === 4 ? "Reveal my estimate" : "Continue"} <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
